import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { AssetFields } from "../../../api/assets";
import { selectChargeById } from "../Logic/chargesSlice";
import { createAsset } from "../Logic/assetsThunk";

import ManagementModal from "../../../components/ui/modals/ManagementModal";
import ReviewChangesModal from "./ReviewChangesModal";
import AssetForm from "./AssetForm";
import { addAssetLinkages } from "../Logic/chargesThunk";
import { ChargeWithLinkages } from "../../../api/charges";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";

interface AddNewAssetModalProps {
  chargeId: string;
  onBack: () => void;
  onClose: () => void;
}

const AddNewAssetModal: React.FC<AddNewAssetModalProps> = ({
  chargeId,
  onBack,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { id: caseId} = useParams<{ id: string }>();

  const existingCharge = useAppSelector((state) =>
    selectChargeById(state, chargeId)
  );

  const [assetInformation, setAssetInformation] = useState<Partial<AssetFields>>({
    asset_class: undefined,
    asset_type: undefined,
    ext_asset_id: "",
    asset_name: "",
  });

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const updatedCharge = useMemo<ChargeWithLinkages | undefined>(() => {
    if (!existingCharge) {
      return undefined;
    }

    const tempAssetLabel = `${assetInformation.asset_name || "New Asset"} (${assetInformation.ext_asset_id})`;

    return {
      ...existingCharge,
      asset_linkages: [
        ...existingCharge.asset_linkages,
        {
          asset_id: tempAssetLabel,
          charge_id: chargeId,
          linkage_id: `temp-${assetInformation.ext_asset_id}`,
        },
      ],
    };
  }, [existingCharge, assetInformation,chargeId]);

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const newAsset = await dispatch(
          createAsset({
            case_id: caseId ?? "",
            request: assetInformation as AssetFields,
          })
      ).unwrap();

      await dispatch(
        addAssetLinkages({
          case_id: caseId ?? "",
          charge_id: chargeId,
          request: {
            asset_linkages: [
              {
                asset_id: newAsset.asset_id,
                charge_id: chargeId,
              },
            ],
          },
        })
      ).unwrap();
      
      showSuccessToast("New asset added successfully.");
      
      onClose();
    } catch (error) {
      showErrorToast("Failed to add new asset. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const isSubmitDisabled =
    !assetInformation.asset_class ||
    !assetInformation.asset_type ||
    !assetInformation.ext_asset_id;

  if (showReviewModal) {
    return (
      <ReviewChangesModal
        existingCharge={existingCharge}
        updatedCharge={updatedCharge}
        onBack={() => setShowReviewModal(false)}
        onClose={onClose}
        onConfirm={handleSave}
        isLoading={isSaving}
      />
    );
  }

  return (
    <ManagementModal
      title="Add New Asset"
      onClose={onClose}
      onBack={onBack}
      submitLabel="Review Changes"
      onSubmit={() => setShowReviewModal(true)}
      submitEnabled={!isSubmitDisabled}
      isLoading={isSaving}
    >
      <div className="">
        <AssetForm
          values={assetInformation}
          onValuesChange={setAssetInformation}
        />
      </div>
    </ManagementModal>
  );
};

export default AddNewAssetModal;